/**
 * JotRx Patient Card
 *
 * Minimal CTA button on the patient detail page.
 * Text and action adapt to the current JotRx onboarding state.
 */

import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@mui/material';
import { useEventTracking } from '@services/statsig';

import { type JotRxSetupState, useJotRxSetupState } from '../hooks/useJotRxSetupState';
import { useLazyGetClinicianSsoQuery } from '../store';

function getButtonLabel(state: JotRxSetupState): string {
  switch (state) {
    case 'not_connected':
    case 'practice_connected':
      return 'Set Up E-Prescribing';
    case 'registered':
      return 'Complete ID Proofing to ePrescribe!';
    case 'idp_complete':
      return 'Setup TFA to ePrescribe!';
    case 'fully_setup':
      return 'Send Prescription';
  }
}

function shouldOpenSso(state: JotRxSetupState): boolean {
  return state === 'fully_setup';
}

export function JotRxPatientCard() {
  const navigate = useNavigate();
  const { setupState, isJotRxEnabled, isLoading, cta } = useJotRxSetupState();
  const { jotrx } = useEventTracking();
  const [getClinicianSso] = useLazyGetClinicianSsoQuery();

  const isVisible = isJotRxEnabled && !isLoading;

  const handleClick = useCallback(async () => {
    jotrx.setupStarted({ source: 'patient_detail', setup_state: setupState });

    if (shouldOpenSso(setupState)) {
      try {
        const result = await getClinicianSso().unwrap();
        if (result.sso_url) {
          window.open(result.sso_url, '_blank', 'noopener,noreferrer');
          return;
        }
      } catch {
        // Fall through to navigate if SSO fails
      }
    }

    navigate(cta.route);
  }, [jotrx, setupState, getClinicianSso, cta.route, navigate]);

  if (!isVisible) return null;

  return (
    <Button
      variant="contained"
      fullWidth
      color="secondary"
      onClick={handleClick}
      sx={{
        py: 1.25,
        fontWeight: 600,
        borderRadius: 1.5,
        textTransform: 'none',
        boxShadow: 1,
      }}
    >
      {getButtonLabel(setupState)}
    </Button>
  );
}
